import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import {
  AlarmClock,
  CircleCheck,
  EllipsisVertical,
  Play,
  RotateCcw,
  ThumbsDown,
  ThumbsUp,
} from "lucide-mui";
import { useRef, useState } from "react";
import ConfirmDialog from "../ConfirmDialog.tsx";
import DropdownMenu from "../DropdownMenu.tsx";
import FormattedDate from "../FormattedDate.tsx";
import type { RowAction } from "../RowActions.tsx";
import ReviewDecisionDialog from "./ReviewDecisionDialog.tsx";
import { SnoozeMenuPopover } from "./SnoozeMenu.tsx";
import { canMarkDone, canReview, canSnooze } from "./rules.ts";
import { isSnoozed } from "./snooze.ts";
import type { ReviewCopy, ReviewDecision, ReviewSubject, UseReview } from "./types.ts";

/**
 * One row's actions; none of them opens the row. The review decision stays
 * inline: Pursue and Disqualify, which ask for feedback first; clicking either
 * again takes the decision back. Continue, Done and Snooze (the worklist's
 * rows) sit in the kebab, then the page's own `extraActions` (a collection's
 * "Remove from collection"), destructive ones confirming first.
 */
export default function ReviewRowActions({
  subject,
  name,
  canDecide,
  canContinue = canDecide,
  useReview,
  copy,
  extraActions = [],
}: {
  subject: ReviewSubject;
  // For the kebab's aria-label.
  name: string;
  canDecide: boolean;
  // Continuing a parked step may need a scope of its own.
  canContinue?: boolean;
  useReview: UseReview;
  copy: ReviewCopy;
  extraActions?: RowAction[];
}) {
  const { continueSteps, review, snooze, done, busy } = useReview();
  // Kept after closing so the dialog's text doesn't blank while it fades out.
  const [confirming, setConfirming] = useState<RowAction | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPending, setConfirmPending] = useState(false);
  const [decision, setDecision] = useState<ReviewDecision | null>(null);
  // The snooze menu opens where the kebab was, once the kebab's menu has closed.
  const anchorRef = useRef<HTMLDivElement>(null);
  const [snoozeAnchor, setSnoozeAnchor] = useState<HTMLElement | null>(null);
  if (!canDecide) return null;
  const reviewable = canReview(subject);
  const pursued = subject.review_state === "accepted";
  const isDone = !!subject.done_at;
  const dropped = subject.review_state === "disqualified";
  const finishable = canMarkDone(subject);
  const snoozed = isSnoozed(subject.snoozed_until);
  const ids = [subject.id];
  const extras = extraActions.filter((a) => !a.hidden);
  const run = (action: RowAction) => {
    if (action.confirm) {
      setConfirming(action);
      setConfirmOpen(true);
      return;
    }
    // A rejected mutateAsync has already toasted its error.
    void Promise.resolve(action.onClick()).catch(() => {});
  };
  const confirm = async () => {
    if (!confirming) return;
    setConfirmPending(true);
    try {
      await confirming.onClick();
      setConfirmOpen(false);
    } catch {
      // The mutation's own error toast says what went wrong; keep the dialog open.
    } finally {
      setConfirmPending(false);
    }
  };
  return (
    <Box
      ref={anchorRef}
      sx={{ display: "inline-flex", alignItems: "center" }}
      onClick={(e) => e.stopPropagation()}
    >
      <Tooltip
        title={reviewable ? (pursued ? "Pursuing · click to reset" : "Pursue") : copy.notReviewable}
      >
        <span>
          <IconButton
            size="small"
            color={pursued ? "success" : "default"}
            disabled={busy || !reviewable}
            onClick={() =>
              pursued
                ? review.mutate({ ids, action: "reset" })
                : setDecision({ ids, action: "accept" })
            }
            aria-label="Pursue"
          >
            <ThumbsUp fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip
        title={
          reviewable ? (dropped ? "Disqualified · click to reset" : "Disqualify") : copy.notReviewable
        }
      >
        <span>
          <IconButton
            size="small"
            color={dropped ? "error" : "default"}
            disabled={busy || !reviewable}
            onClick={() =>
              dropped
                ? review.mutate({ ids, action: "reset" })
                : setDecision({ ids, action: "disqualify" })
            }
            aria-label="Disqualify"
          >
            <ThumbsDown fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <DropdownMenu
        aria-label={`More actions for ${name}`}
        icon={<EllipsisVertical fontSize="small" />}
        align="right"
        buttonProps={{ size: "small" }}
      >
        {subject.awaiting_approval && canContinue && (
          <MenuItem disabled={busy} onClick={() => continueSteps.mutate(ids)}>
            <ListItemIcon>
              <Play fontSize="small" />
            </ListItemIcon>
            <ListItemText>Continue the workflow</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          disabled={busy || !finishable}
          onClick={() => done.mutate({ ids, done: !isDone })}
        >
          <ListItemIcon>
            {isDone ? <RotateCcw fontSize="small" /> : <CircleCheck fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary={isDone ? "Reopen" : "Mark done"}
            secondary={finishable ? undefined : copy.notOnWorklist}
            slotProps={{ secondary: { sx: { maxWidth: 220, whiteSpace: "normal" } } }}
          />
        </MenuItem>
        {canSnooze(subject) && (
          <MenuItem disabled={busy} onClick={() => setSnoozeAnchor(anchorRef.current)}>
            <ListItemIcon>
              <AlarmClock fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={snoozed ? "Change snooze" : "Snooze"}
              secondary={
                snoozed ? (
                  <>
                    Until <FormattedDate value={subject.snoozed_until!} />
                  </>
                ) : undefined
              }
            />
          </MenuItem>
        )}
        {extras.length > 0 && <Divider />}
        {extras.map((action) => (
          <MenuItem
            key={action.label}
            disabled={action.disabled}
            onClick={() => run(action)}
            sx={action.destructive ? { color: "error.main" } : undefined}
          >
            {action.icon && (
              <ListItemIcon sx={action.destructive ? { color: "inherit" } : undefined}>{action.icon}</ListItemIcon>
            )}
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </DropdownMenu>
      <SnoozeMenuPopover
        anchor={snoozeAnchor}
        onClose={() => setSnoozeAnchor(null)}
        snoozedUntil={subject.snoozed_until}
        onSnooze={(until) => snooze.mutate({ ids, until })}
      />
      <ReviewDecisionDialog
        decision={decision}
        onClose={() => setDecision(null)}
        useReview={useReview}
        copy={copy}
      />
      <ConfirmDialog
        open={confirmOpen}
        title={confirming?.confirm?.title ?? ""}
        confirmLabel={confirming?.confirm?.confirmLabel ?? confirming?.label}
        cancelLabel={confirming?.confirm?.cancelLabel}
        color={confirming?.confirm?.color ?? (confirming?.destructive ? "error" : "warning")}
        pending={confirmPending}
        onConfirm={confirm}
        onClose={() => setConfirmOpen(false)}
      >
        {confirming?.confirm?.body}
      </ConfirmDialog>
    </Box>
  );
}
